import { getMonth, isAfter, isValid, parse, parseISO, startOfDay } from 'date-fns';
import { numericMonth } from '@shared/models/types/date.type';
import { isoDateYYYYMMDDRegex, legacySlashDateRegex, yearsRangeRegex } from '@shared/constants/regex.const';

export const isFutureDay = (dateStr: string): boolean => {
    const d = parseISO(dateStr);
    if (Number.isNaN(d.getTime())) return false;
    return isAfter(startOfDay(d), startOfDay(new Date()));
};

export const isBeforeMonth = (dateStr: string, month: numericMonth): boolean => {
    if (!dateStr || month < 1 || month > 12) {
        return false;
    }

    const d = parseISO(dateStr);
    const m = getMonth(d) + 1;
    if (m < 1 || m > 12) {
        return false;
    }

    return m <= month;
};

export const toJsDateMsOrNull = (value: string): number | null => {
    const s = (value ?? '').trim();
    if (!s) {
        return null;
    }

    if (isoDateYYYYMMDDRegex.test(s)) {
        const d = parse(s, 'yyyy-MM-dd', new Date());
        return isValid(d) ? d.getTime() : null;
    }

    const d = new Date(s);
    const ms = d.getTime();
    return Number.isFinite(ms) ? ms : null;
};

export const toYearRangeOrNull = (value: string): { start: number; end: number } | null => {
    const s = (value ?? '').trim();
    const match = yearsRangeRegex.exec(s);

    if (!match) {
        return null;
    }

    const start = Number(match[1]);
    const end = Number(match[2]);

    if (!Number.isSafeInteger(start) || !Number.isSafeInteger(end)) {
        return null;
    }

    return { start, end };
};

/** Returns true if value (format dd/mm or dd-mm) represents a valid calendar day/month combination. */
export const isValidDayMonth = (value: string): boolean => {
    const day = parseInt(value.substring(0, 2), 10);
    const month = parseInt(value.substring(3, 5), 10);
    if (isNaN(day) || isNaN(month)) {
        return false;
    }
    const date = new Date(1900, month - 1, day);
    return date.getMonth() === month - 1 && date.getDate() === day;
};

/**
 * Converte un valore data legacy in formato yyyy/MM/dd (es. da import XML/InfoPath) nel formato
 * ISO yyyy-MM-dd atteso da <input type="date">. Ritorna il valore invariato se non corrisponde al pattern legacy.
 */
export const normalizeLegacyDateSeparator = (value: string): string => {
    const raw = (value ?? '').toString().trim();
    return legacySlashDateRegex.test(raw) ? raw.replace(/\//g, '-') : value;
};
